/**
 * MarketTrendsChart Component
 * 
 * Time-series chart displaying market trends using ECharts
 * Shows median price trends and Market Action Index over time
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, ButtonGroup, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine, 
  faCalendarAlt, 
  faExpandArrowsAlt,
  faDownload,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import EChartsReact from 'echarts-for-react';
import { EChartsOption } from 'echarts-for-react';

// Import types
import type { 
  MarketTrendsChartProps, 
  MarketTrend 
} from '../../../../types/marketIntelligence.types';

// Period options
const PERIOD_OPTIONS = [
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '180d', label: '6 Months' },
  { value: '1y', label: '1 Year' },
  { value: '2y', label: '2 Years' }
];

// Chart type options
const CHART_TYPES = [
  { value: 'price', label: 'Median Price', color: '#10b981' },
  { value: 'mai', label: 'Market Action Index', color: '#3b82f6' },
  { value: 'volume', label: 'Sales Volume', color: '#f59e0b' },
  { value: 'combined', label: 'Combined View', color: '#8b5cf6' }
];

// Mock data generator
const generateMockData = (zipCode: string, period: string): MarketTrend[] => {
  const days = period === '30d' ? 30 : 
                period === '90d' ? 90 : 
                period === '180d' ? 180 : 
                period === '1y' ? 365 : 730;
  
  const data: MarketTrend[] = [];
  const basePrice = 250000 + Math.random() * 200000;
  const baseMai = 50 + Math.random() * 30;
  
  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    
    // Add some realistic variation
    const priceVariation = (Math.random() - 0.5) * 0.1; // ±10%
    const maiVariation = (Math.random() - 0.5) * 0.2; // ±20%
    
    data.push({
      analysis_date: date.toISOString().split('T')[0],
      median_close_price: Math.round(basePrice * (1 + priceVariation)),
      market_action_index: Math.max(0, Math.min(100, baseMai + maiVariation * 20)),
      daily_sales_count: Math.floor(Math.random() * 10) + 1,
      total_volume: Math.round((basePrice * (1 + priceVariation)) * (Math.random() * 5 + 1)),
      market_temperature: ['Hot Seller Market', 'Strong Market', 'Balanced Market'][Math.floor(Math.random() * 3)] as any
    });
  }
  
  return data;
};

const MarketTrendsChart: React.FC<MarketTrendsChartProps> = ({
  zipCode,
  period = '90d',
  height = 300,
  showTooltips = true
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState(period);
  const [chartType, setChartType] = useState('combined');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MarketTrend[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch data when zipCode or period changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // TODO: Replace with actual API call
        // const response = await axios.get(`/api/market-intelligence/zip/${zipCode}/trends?period=${selectedPeriod}`);
        // setData(response.data.data.trend_data);
        
        const mockData = generateMockData(zipCode, selectedPeriod);
        setData(mockData);
      } catch (err) {
        setError('Failed to load trend data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [zipCode, selectedPeriod]);

  // ECharts configuration
  const chartOptions: EChartsOption = useMemo(() => {
    if (!data || data.length === 0) {
      return {};
    }

    const dates = data.map(d => d.analysis_date);
    const prices = data.map(d => d.median_close_price);
    const mai = data.map(d => d.market_action_index);
    const volume = data.map(d => d.total_volume);

    const baseOptions: EChartsOption = {
        title: {
        text: `Market Trends - ZIP ${zipCode}`,
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
          color: '#1f2937'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        },
        formatter: function(params: any) {
          let result = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach((param: any) => {
            const value = param.seriesName.includes('Price') ? 
              `$${param.value.toLocaleString()}` : 
              param.seriesName.includes('Volume') ?
              `$${(param.value / 1000000).toFixed(1)}M` :
              `${param.value.toFixed(1)}`;
            result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
          });
          return result;
        }
      },
      legend: {
        data: chartType === 'combined' ? 
          ['Median Price', 'Market Action Index', 'Sales Volume'] :
          chartType === 'price' ? ['Median Price'] :
          chartType === 'mai' ? ['Market Action Index'] :
          ['Sales Volume'],
        top: 40
        },
        grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLabel: {
          formatter: function(value: string) {
            return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
        }
      },
      yAxis: [],
      series: []
    };

    // Configure based on chart type
    if (chartType === 'price' || chartType === 'combined') {
      baseOptions.yAxis.push({
        type: 'value',
        name: 'Price',
        position: 'left',
        axisLabel: {
          formatter: (value: number) => `$${value.toLocaleString()}`
        }
      });
      baseOptions.series.push({
        name: 'Median Price',
        type: 'line',
        yAxisIndex: 0,
        smooth: true,
        itemStyle: { color: '#10b981' },
        lineStyle: { width: 3 },
        data: prices
      });
    }

    if (chartType === 'mai' || chartType === 'combined') {
      if (chartType === 'combined') {
        baseOptions.yAxis.push({
          type: 'value',
          name: 'MAI',
          position: 'right',
          min: 0,
          max: 100,
          axisLabel: {
            formatter: (value: number) => `${value}`
        }
        });
      } else {
        baseOptions.yAxis.push({
          type: 'value',
          name: 'Market Action Index',
          min: 0,
          max: 100,
          axisLabel: {
            formatter: (value: number) => `${value}`
          }
        });
      }
      baseOptions.series.push({
        name: 'Market Action Index',
        type: 'line',
        yAxisIndex: chartType === 'combined' ? 1 : 0,
        smooth: true,
        itemStyle: { color: '#3b82f6' },
        lineStyle: { width: 2 },
        data: mai
      });
    }

    if (chartType === 'volume' || chartType === 'combined') {
      if (chartType === 'combined') {
        // Volume as area chart for combined view
        baseOptions.series.push({
          name: 'Sales Volume',
          type: 'line',
          yAxisIndex: 0,
          smooth: true,
          itemStyle: { color: '#f59e0b' },
          lineStyle: { width: 2, type: 'dashed' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(245, 158, 11, 0.3)' },
                { offset: 1, color: 'rgba(245, 158, 11, 0.1)' }
              ]
            }
          },
          data: volume
        });
      } else {
        baseOptions.yAxis.push({
          type: 'value',
          name: 'Volume',
          axisLabel: {
            formatter: (value: number) => `$${value.toLocaleString()}`
          }
        });
        baseOptions.series.push({
          name: 'Sales Volume',
          type: 'bar',
          itemStyle: { color: '#f59e0b' },
          data: volume
        });
    }
    }

    return baseOptions;
  }, [data, chartType, zipCode]);

  // Export functionality
  const exportChart = () => {
    // Implementation for chart export
    console.log('Export chart functionality');
  };

  if (loading) {
  return (
      <Card style={{ 
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '16px',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        height: height + 100
      }}>
        <Card.Body className="d-flex align-items-center justify-content-center">
          <div className="text-center">
            <FontAwesomeIcon 
              icon={faSpinner} 
              spin 
              size="2x" 
              style={{ color: '#00d4ff', marginBottom: '16px' }}
            />
            <div style={{ color: '#6b7280' }}>Loading market trends...</div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ 
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '16px',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        backdropFilter: 'blur(10px)',
        height: height + 100
      }}>
        <Card.Body className="d-flex align-items-center justify-content-center">
          <div className="text-center">
            <div style={{ color: '#ef4444' }}>{error}</div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card style={{ 
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '16px',
      border: '1px solid rgba(0, 212, 255, 0.2)',
      backdropFilter: 'blur(10px)'
    }}>
      <Card.Header style={{ 
        background: 'transparent',
        border: 'none',
        padding: '20px 20px 10px 20px'
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faChartLine} 
              style={{ color: '#00d4ff', marginRight: '12px' }}
            />
            <h6 style={{ color: '#1f2937', fontWeight: '600', marginBottom: 0 }}>
              Market Trends Analysis
            </h6>
          </div>
          
          <div className="d-flex align-items-center gap-2">
            <Form.Select 
              size="sm" 
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              style={{ width: 'auto', minWidth: '120px' }}
            >
              {PERIOD_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            
            <ButtonGroup size="sm">
              {CHART_TYPES.map(type => (
                <Button
                  key={type.value}
                  variant={chartType === type.value ? "primary" : "outline-primary"}
                  onClick={() => setChartType(type.value)}
                  style={{ fontSize: '12px' }}
                >
                  {type.label}
                </Button>
              ))}
            </ButtonGroup>
            
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={exportChart}
            >
              <FontAwesomeIcon icon={faDownload} />
            </Button>
          </div>
        </div>
      </Card.Header>

      <Card.Body style={{ padding: '10px 20px 20px 20px' }}>
        <EChartsReact
          option={chartOptions}
          style={{ height: `${height}px`, width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
        
        {showTooltips && (
          <div className="mt-3" style={{ fontSize: '12px', color: '#6b7280' }}>
            <div className="d-flex justify-content-between">
              <span>Data Points: {data.length}</span>
              <span>Period: {PERIOD_OPTIONS.find(p => p.value === selectedPeriod)?.label}</span>
              <span>ZIP Code: {zipCode}</span>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default MarketTrendsChart;